/*
    Segment maths for the Budget screen, ported from the Budget Segments prototype
    (`budget-data.jsx` in the Claude Design project). Function names are kept as the
    prototype has them so the two can be diffed by eye.

    The prototype reads `window.CAMPAIGN` / `window.ENGAGEMENT` at call time; here every
    input is a parameter, so the engine is pure and testable. That is the only intentional
    departure — the arithmetic is unchanged.
*/

export type SpendPattern = "even" | "monthly" | "custom"

export type Segment = {
  id: string
  /** Inclusive YYYY-MM-DD bounds. */
  start: string
  end: string
  /** Dollars, net of margin. Segments sum to the campaign's Amount, not to 100%. */
  amount: number
  /**
   * Networks an operator set by hand on THIS segment, key -> percent. An absent key is
   * "auto" and reads through to the campaign default mix, so one pinned network can carry
   * forward without freezing the whole mix.
   */
  pinned: Record<string, number>
  /** Committed per-segment pause overrides, set on Save. Absent key = inherit network status. */
  netOn?: Record<string, boolean>
  agOn?: Record<string, boolean>
}

export type Flight = { start: string; end: string }

export const DAY = 86400000

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/**
 * Milliseconds for a YYYY-MM-DD day. UTC throughout, so a local timezone never shifts a
 * date: a campaign that starts on the 18th starts on the 18th in Perth and in Boston.
 */
export const D = (day: string): number => {
  const [year, month, dayOfMonth] = day.split("-").map(Number)
  return Date.UTC(year, month - 1, dayOfMonth)
}

export const iso = (milliseconds: number): string => {
  const parsed = new Date(milliseconds)
  return (
    parsed.getUTCFullYear() +
    "-" +
    String(parsed.getUTCMonth() + 1).padStart(2, "0") +
    "-" +
    String(parsed.getUTCDate()).padStart(2, "0")
  )
}

export const addDays = (day: string, count: number): string => iso(D(day) + count * DAY)

/** Inclusive of both ends, matching how a flight is counted: 1 Sep to 1 Sep is one day. */
export const dayCount = (from: string, to: string): number =>
  Math.round((D(to) - D(from)) / DAY) + 1

export const fmtDay = (day: string): string => {
  const parsed = new Date(D(day))
  return MONTHS[parsed.getUTCMonth()] + " " + parsed.getUTCDate()
}

export const fmtRange = (from: string, to: string): string => {
  const start = new Date(D(from))
  const end = new Date(D(to))
  return start.getUTCMonth() === end.getUTCMonth() &&
    start.getUTCFullYear() === end.getUTCFullYear()
    ? MONTHS[start.getUTCMonth()] + " " + start.getUTCDate() + "–" + end.getUTCDate()
    : fmtDay(from) + " – " + fmtDay(to)
}

export const pct2 = (value: number): string => (Math.round((Number(value) || 0) * 100) / 100).toFixed(2)

const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Amount = Allocation × (1 − Margin).
 *
 * Allocation is the campaign's own share of the engagement, set on the Campaigns list. There is no
 * separate back-solved gross: that was the defect behind Amount swinging to $44,230.77 on an
 * 87%→50% margin edit, and it means Amount can never exceed the allocation.
 */
export const netAmount = (allocation: number, marginPct: number): number =>
  (allocation || 0) * (1 - (marginPct || 0) / 100)

export type ReflowResult = {
  segments: Segment[]
  moved: number
  count: number
}

/**
 * Spread a new net total across the segments that have not finished yet.
 *
 * ⚠️ A MIRROR, NOT THE AUTHORITY. `framework/policy/spend_pattern.reflow_for_net` is what
 * actually writes the dollars; this exists so the Timeline stays responsive, because its
 * drag handles fire on every mousemove and the daily figure and floor check have to
 * recompute for a window the server has never seen. Change the rule there first, then
 * here, and let `spendPatternParity.test.ts` confirm the two still agree.
 *
 * Elapsed segments (end before today) are history and keep their dollars; the remainder is
 * split across current and upcoming segments in proportion to what they already hold, or
 * evenly when they hold nothing. Custom-mode gaps are absences rather than records, so there
 * is nothing to redistribute into.
 *
 * The Python docstring carries the worked example and the reasoning behind each branch.
 */
/**
 * Cut a segment that straddles today into its elapsed half and its remaining half.
 *
 * Decided 10/9: a change applies from today forward. Without the cut, a segment running
 * 1–31 Oct is wholly "live" on the 14th — its end has not passed — and a margin change
 * rewrites thirteen days that already spent. The elapsed half keeps its dollars, prorated
 * by day; the id stays with it, because that half IS the stored row continuing to exist.
 *
 * Only a STORED segment is cut, matching the server: the cut preserves dollars that have
 * already spent, and a segment the server has never seen has none. A numeric id is what
 * marks one as stored — `toSegments` carries the server's `segment_id` through as a string,
 * while a segment drawn here gets "s1", "s2". The save path tells them apart the same way.
 *
 * Mirrors `split_at_today` in framework/policy/spend_pattern.py.
 */
const isStored = (id: string): boolean => /^\d+$/.test(id)

export const splitAtToday = (segments: Segment[], today: string): Segment[] =>
  segments.flatMap((segment) => {
    const straddles = D(segment.start) < D(today) && D(today) <= D(segment.end)
    if (!straddles || !isStored(segment.id)) return [segment]

    const days = dayCount(segment.start, segment.end)
    const elapsedDays = Math.round((D(today) - D(segment.start)) / DAY)
    // In whole cents, multiplied before divided. Dividing a float first leaves a repeating
    // decimal that rounds differently from the server's Decimal — a one-cent disagreement
    // on roughly one split in two hundred, which the shared fixture happened not to cover.
    const cents = Math.round(segment.amount * 100)
    const elapsedAmount = Math.round((cents * elapsedDays) / days) / 100

    return [
      { ...segment, end: addDays(today, -1), amount: elapsedAmount },
      {
        ...segment,
        id: "s" + seq++,
        start: today,
        amount: round2(segment.amount - elapsedAmount),
      },
    ]
  })

export const reflowForNet = (input: Segment[], newNet: number, today: string): ReflowResult => {
  const segments = splitAtToday(input, today)
  const live = segments.filter((segment) => D(segment.end) >= D(today))
  if (!live.length) return { segments, moved: 0, count: 0 }

  const elapsedTotal = segments
    .filter((segment) => D(segment.end) < D(today))
    .reduce((total, segment) => total + segment.amount, 0)
  const liveTotal = live.reduce((total, segment) => total + segment.amount, 0)
  const target = Math.max(0, newNet - elapsedTotal)

  const reflowed = segments.map((segment) => {
    if (D(segment.end) < D(today)) return segment
    const share = liveTotal > 0 ? segment.amount / liveTotal : 1 / live.length
    return { ...segment, amount: round2(target * share) }
  })
  return { segments: reflowed, moved: target - liveTotal, count: live.length }
}

/**
 * The same proportional rule applied to a network mix instead of segments: each active
 * network is a live pseudo-segment whose "amount" is its percent, and the target is the
 * current total plus the share freed by whatever was paused.
 */
export const reflowMix = (
  mix: Record<string, number>,
  pausedNetworks: string[],
  activeNetworks: string[],
): Record<string, number> => {
  const freedTotal = pausedNetworks.reduce((total, network) => total + (mix[network] || 0), 0)
  // A fixed date for all of them: nothing here is elapsed, so the date is inert and only
  // has to be consistent.
  const epoch = "1970-01-01"
  const asSegments: Segment[] = activeNetworks.map((network) => ({
    id: network,
    start: epoch,
    end: epoch,
    amount: mix[network] || 0,
    pinned: {},
  }))
  const currentTotal = asSegments.reduce((total, network) => total + network.amount, 0)

  const out: Record<string, number> = {}
  reflowForNet(asSegments, currentTotal + freedTotal, epoch).segments.forEach((network) => {
    out[network.id] = network.amount
  })
  return out
}

/**
 * Scheduled serving days inside a window.
 *
 * The divisor for a window's daily rate, and deliberately the same rule the server applies in
 * `_compute_days`: clip each serving range to the window, count the days, never count a day
 * twice. The ranges come down with the budget tree per network, because a network can narrow
 * the campaign's schedule and 28 of 144 ad-group schedules in production do.
 *
 * Why this is computed here at all, rather than read off the server's `active_days`: an
 * operator drags a window's edge on the timeline and the daily figure recomputes on every
 * mousemove, for a window the server has never resolved. The floor check blocks Save on that
 * figure, so it has to be right while the mouse is still down.
 *
 * `activeDaysFor` is pinned against the server in test_segment_daily_rate.py, which counts
 * the days this way and asserts the result equals the `active_days` the resolver returned.
 */
export type ScheduledRange = readonly [start: string, end: string]

export const activeDaysIn = (
  ranges: readonly ScheduledRange[],
  windowStart: string,
  windowEnd: string,
): number => {
  let days = 0
  for (const [rangeStart, rangeEnd] of ranges) {
    const from = rangeStart > windowStart ? rangeStart : windowStart
    const to = rangeEnd < windowEnd ? rangeEnd : windowEnd
    if (from <= to) days += dayCount(from, to)
  }
  return days
}

/**
 * The divisor for one window: `min(scheduled days, funded days)`.
 *
 * Both halves matter and the server's `_per_day` is the same expression. Scheduled days are
 * the days the network actually serves inside the window. Funded days are the engagement's
 * spend window, and they bind when the money arrives on a shorter cycle than the campaign
 * runs over -- 14 of 528 network rows on the production dump, where ignoring it would show a
 * daily figure under half what the network is really given.
 *
 * The fallback to calendar length is for a campaign whose schedule has not been built yet:
 * with no ranges there is no honest smaller answer. NOT the normal path -- every serving
 * campaign has a schedule.
 */
export const payDays = (
  segment: Segment,
  ranges: readonly ScheduledRange[] | undefined,
  fundedDays = 0,
): number => {
  const scheduled = ranges?.length ? activeDaysIn(ranges, segment.start, segment.end) : 0
  const days = scheduled || dayCount(segment.start, segment.end)
  return fundedDays > 0 && fundedDays < days ? fundedDays : days
}

export const dailyRate = (
  segment: Segment,
  ranges: readonly ScheduledRange[] | undefined,
  fundedDays = 0,
): number => segment.amount / payDays(segment, ranges, fundedDays)

let seq = 1

export const seg = (
  start: string,
  end: string,
  amount: number,
  extra?: Partial<Segment>,
): Segment => ({ id: "s" + seq++, start, end, amount, pinned: {}, ...extra })

/** Test seam: ids are compared in snapshots, so a suite needs them to start from a known point. */
export const resetSegmentIds = (): void => {
  seq = 1
}

/** One spend period covering the whole flight. */
export const evenSegments = (flight: Flight, net: number): Segment[] => [
  seg(flight.start, flight.end, round2(net)),
]

/**
 * One segment per calendar month, each seeded at the same daily rate.
 *
 * ⚠️ A mirror of `framework/policy/spend_pattern.monthly_windows`, for preview only —
 * what a Save writes is whatever the server generates. Pinned by spendPatternParity.test.ts.
 */
export const monthlySegments = (flight: Flight, net: number): Segment[] => {
  const months: Segment[] = []
  let monthStart = flight.start
  while (D(monthStart) <= D(flight.end)) {
    const asDate = new Date(D(monthStart))
    const lastOfMonth = iso(Date.UTC(asDate.getUTCFullYear(), asDate.getUTCMonth() + 1, 0))
    const monthEnd = D(lastOfMonth) < D(flight.end) ? lastOfMonth : flight.end
    months.push(seg(monthStart, monthEnd, 0))
    monthStart = addDays(monthEnd, 1)
  }

  const totalDays = months.reduce(
    (total, month) => total + dayCount(month.start, month.end), 0,
  )
  const dailyRateForFlight = net / totalDays
  months.forEach((month) => {
    month.amount = round2(dayCount(month.start, month.end) * dailyRateForFlight)
  })
  // Per-month rounding leaves the sum a cent or two off the campaign's Amount; the last
  // month absorbs it so Allocated reconciles exactly rather than showing a permanent
  // mismatch.
  const drift = round2(net - months.reduce((total, month) => total + month.amount, 0))
  months[months.length - 1].amount = round2(months[months.length - 1].amount + drift)
  return months
}

/**
 * Custom's starting shape: two spend blocks with a real gap between them.
 *
 * The prototype seeds these at 40/34/18 of the net — deliberately short of 100%, so its
 * reconciliation warning is visible on load. That is review scaffolding, not product
 * behaviour, so the closing block takes the remainder here and the preset reconciles.
 */
export const customSegments = (
  flight: Flight,
  net: number,
  pinned: Record<string, number> = {},
): Segment[] => {
  const span = dayCount(flight.start, flight.end)
  const firstEnd = addDays(flight.start, Math.max(6, Math.round(span * 0.28)))
  const gapEnd = addDays(firstEnd, Math.max(6, Math.round(span * 0.14)))
  const secondEnd = addDays(gapEnd, Math.max(6, Math.round(span * 0.28)))

  const a1 = round2(net * 0.4)
  const a2 = round2(net * 0.34)
  return [
    seg(flight.start, firstEnd, a1),
    seg(addDays(gapEnd, 1), secondEnd, a2, { pinned }),
    seg(addDays(secondEnd, 1), flight.end, round2(net - a1 - a2)),
  ]
}

export const buildPattern = (
  pattern: SpendPattern,
  flight: Flight,
  net: number,
  customPins: Record<string, number> = {},
): Segment[] => {
  if (pattern === "even") return evenSegments(flight, net)
  if (pattern === "monthly") return monthlySegments(flight, net)
  return customSegments(flight, net, customPins)
}

/** The mix a segment actually spends on: the campaign default, with its own pins laid over. */
export const effectiveMix = (
  segment: Segment,
  defaultMix: Record<string, number>,
): Record<string, number> => ({ ...defaultMix, ...(segment.pinned || {}) })

export const isPinned = (segment: Segment, networkKey: string): boolean =>
  Object.prototype.hasOwnProperty.call(segment.pinned || {}, networkKey)
